import { alert } from '@/services/alert';
import { ActionIcon, Box, Flex, Group, Menu, Stack, Text } from '@mantine/core';
import { formatHumanName, getReferenceString } from '@medplum/core';
import { ResourceAvatar, useMedplum } from '@medplum/react';
import { IconPencil, IconTrash, IconX } from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { APPOINTMENT_SERIES_IDENTIFIER_SYSTEM } from '@bariendo/lib/constants';
import { Practitioner } from '@medplum/fhirtypes';
import { hasRichTextContent } from '../utils/appointments';
import { BlockSlotDetailData, PopoverPosition } from '../types';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { DeleteConfirmationPopover } from '@/components/DeleteConfirmationPopover';
import { HighlightableRichText } from '@/components/HighlightableText/HighlightableRichText';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { usePopover } from '../hooks/usePopover';

dayjs.extend(utc);
dayjs.extend(timezone);

interface BlockDetailPopoverProps {
  opened: boolean;
  onClose: () => void;
  block: BlockSlotDetailData;
  selectedTimezone: string;
  position: PopoverPosition;
  onEdit?: () => void;
  onDeleted?: () => void;
}

export function BlockDetailPopover({
  opened,
  onClose,
  block,
  selectedTimezone,
  position,
  onEdit,
  onDeleted,
}: BlockDetailPopoverProps) {
  const medplum = useMedplum();
  const {
    currentUserPermissions: { canManageSchedules },
    currentUser,
  } = useCurrentUser();
  const { adjustedPosition, arrowSide, popoverWidth, popoverRef } = usePopover(opened, position, onClose);
  const [isDeleting, setIsDeleting] = useState(false);

  const slot = block.slotResource;
  const isSeries = slot.identifier?.some((id) => id.system === APPOINTMENT_SERIES_IDENTIFIER_SYSTEM) ?? false;

  const currentUserRef = currentUser ? getReferenceString(currentUser as Practitioner) : undefined;
  const isBlockProvider =
    !!currentUserRef && block.providers.some((p) => getReferenceString(p as { reference?: string }) === currentUserRef);
  const canEdit = isBlockProvider || canManageSchedules;

  const deleteThisSlot = useCallback(async () => {
    if (!slot.id) {
      alert.error('Cannot delete block: missing ID');
      return;
    }
    setIsDeleting(true);
    try {
      await medplum.deleteResource('Slot', slot.id);
      alert.success('Block deleted successfully');
      onDeleted?.();
      onClose();
    } catch (error) {
      alert.error('Failed to delete block: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsDeleting(false);
    }
  }, [medplum, slot.id, onDeleted, onClose]);

  /** Deletes this slot and every later occurrence in the same series (same identifier), not past dates. */
  const deleteSeriesForward = useCallback(async () => {
    const seriesId = slot.identifier?.find((i) => i.system === APPOINTMENT_SERIES_IDENTIFIER_SYSTEM)?.value;
    const currentStart = slot.start;
    if (!seriesId || !slot.id || !currentStart) {
      await deleteThisSlot();
      return;
    }
    const pivotMs = dayjs(currentStart).valueOf();

    setIsDeleting(true);
    try {
      const identifierToken = `${APPOINTMENT_SERIES_IDENTIFIER_SYSTEM}|${seriesId}`;

      const serverResults = await medplum.searchResources('Slot', {
        identifier: identifierToken,
        status: 'busy-unavailable',
        start: `ge${currentStart}`,
        _count: '1000',
      });
      let candidates = Array.from(serverResults);

      if (candidates.length === 0) {
        const allSlots = await medplum.searchResources('Slot', {});
        candidates = Array.from(allSlots).filter(
          (s) =>
            s.status === 'busy-unavailable' &&
            s.identifier?.some((i) => i.system === APPOINTMENT_SERIES_IDENTIFIER_SYSTEM && i.value === seriesId)
        );
      }

      const toDelete = candidates.filter((s) => s.start && dayjs(s.start).valueOf() >= pivotMs && s.id);

      for (const s of toDelete) {
        if (s.id) await medplum.deleteResource('Slot', s.id);
      }
      alert.success('This block and future occurrences were deleted');
      onDeleted?.();
      onClose();
    } catch (error) {
      alert.error(
        'Failed to delete this block and future occurrences: ' +
          (error instanceof Error ? error.message : 'Unknown error')
      );
    } finally {
      setIsDeleting(false);
    }
  }, [medplum, slot, deleteThisSlot, onDeleted, onClose]);

  const comment = slot.comment;
  const richTextContent = useMemo(() => {
    if (!comment) return null;
    try {
      return JSON.parse(comment);
    } catch {
      return null;
    }
  }, [comment]);

  const formatTime = (start: string | undefined, end: string | undefined): string => {
    if (!start) return '';
    const startTime = dayjs(start).tz(selectedTimezone).format('h:mma').replace(':00', '');
    if (!end) return startTime;
    const endTime = dayjs(end).tz(selectedTimezone).format('h:mma').replace(':00', '');
    return `${startTime} - ${endTime}`;
  };

  const title = block.title ?? 'Block';
  const dateStr = slot.start ? dayjs(slot.start).tz(selectedTimezone).format('MMMM D, YYYY') : '';
  const timeStr = formatTime(slot.start, slot.end);

  if (!opened) return null;

  const popoverContent = (
    <Box
      ref={popoverRef}
      style={{
        position: 'fixed',
        left: adjustedPosition.x,
        top: adjustedPosition.y,
        transform: 'translateY(-50%)',
        zIndex: 1000,
        width: popoverWidth,
        maxWidth: 'calc(100vw - 32px)',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: '16px',
        pointerEvents: 'auto',
      }}
    >
      {arrowSide === 'left' && (
        <Box
          style={{
            position: 'absolute',
            left: '-8px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: 0,
            height: 0,
            borderTop: '8px solid transparent',
            borderBottom: '8px solid transparent',
            borderRight: '8px solid white',
            filter: 'drop-shadow(-2px 0 2px rgba(0, 0, 0, 0.1))',
          }}
        />
      )}
      {arrowSide === 'right' && (
        <Box
          style={{
            position: 'absolute',
            right: '-8px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: 0,
            height: 0,
            borderTop: '8px solid transparent',
            borderBottom: '8px solid transparent',
            borderLeft: '8px solid white',
            filter: 'drop-shadow(2px 0 2px rgba(0, 0, 0, 0.1))',
          }}
        />
      )}
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Box flex={1} maw="100%" style={{ minWidth: 0 }}>
            <Text fw={700} c="blue" td="underline" size="sm">
              {title}
            </Text>
            <Text fw={500} mt={4} size="sm">
              {dateStr} • {timeStr}
            </Text>
          </Box>
          <Group gap="xs" style={{ flexShrink: 0 }} wrap="nowrap" align="center">
            {canEdit &&
              (isSeries ? (
                <Menu position="bottom-end" withinPortal={false}>
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Delete block">
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown style={{ zIndex: 1100 }} py="xs">
                    <Menu.Item onClick={() => void deleteThisSlot()} disabled={isDeleting}>
                      Delete this occurrence
                    </Menu.Item>
                    <Menu.Item onClick={() => void deleteSeriesForward()} disabled={isDeleting}>
                      Delete this and upcoming occurrences
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              ) : (
                <DeleteConfirmationPopover
                  onConfirm={deleteThisSlot}
                  isLoading={isDeleting}
                  title="Delete this block?"
                  description="You cannot undo this action."
                  opened={undefined}
                  onClose={undefined}
                >
                  <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Delete block">
                    <IconTrash size={16} />
                  </ActionIcon>
                </DeleteConfirmationPopover>
              ))}
            {onEdit && (
              <ActionIcon variant="subtle" color="gray" size="sm" onClick={onEdit}>
                <IconPencil size={16} />
              </ActionIcon>
            )}
            <ActionIcon variant="subtle" color="gray" size="sm" onClick={onClose}>
              <IconX size={16} />
            </ActionIcon>
          </Group>
        </Group>

        {block.providers.length > 0 && (
          <Flex gap="md" align="flex-start">
            <Text fw={500} w={140} size="sm">
              Provider
            </Text>
            <Group gap="xs" wrap="nowrap" align="center">
              {block.providers.map((p, idx) => (
                <Group key={idx} gap="xs" wrap="nowrap" align="center">
                  <ResourceAvatar value={p} radius="xl" size="sm" />
                  <Text size="sm" truncate maw={180}>
                    {'name' in p && p.name
                      ? formatHumanName(p.name[0])
                      : 'display' in p && p.display
                        ? p.display
                        : getReferenceString(p)}
                  </Text>
                </Group>
              ))}
            </Group>
          </Flex>
        )}

        {comment && !(richTextContent && !hasRichTextContent(richTextContent)) && (
          <Flex gap="md" align="flex-start">
            <Text fw={500} w={140} size="sm">
              Notes
            </Text>
            <Box flex={1} fz="xs">
              {richTextContent ? (
                <HighlightableRichText richTextContent={richTextContent} text="" searchTerm="" size="sm" />
              ) : (
                <Text size="sm">{comment}</Text>
              )}
            </Box>
          </Flex>
        )}
      </Stack>
    </Box>
  );

  return createPortal(popoverContent, document.body);
}
